import {
  ChevronsUpDown,
  History,
  Info,
  LayoutDashboardIcon,
  LogOut,
  MonitorUp,
  Paintbrush,
  Send,
  Settings,
  Sparkles,
  X,
  LucideIcon,
  Bell,
  BellDot,
  Code2,
  Library,
  PanelLeft,
  UserRound,
  Users,
} from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'

import { ManagePlanButton } from '@/components/common/manage-plan-button'
import { EscalationIconWithCount } from '@/components/icon/encapsulated/escalation-icon'
import { NotificationManagement } from '@/components/notification/notification-management'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'

import { PermissionAction, PermissionResource } from '@/constants/user'
import { useAuth } from '@/hooks/use-auth'
import { usePermission } from '@/hooks/use-permission'
import { useSidebarVisibility } from '@/hooks/use-sidebar-visibility'
import { useSignOut } from '@/hooks/use-sign-out'
import { cn } from '@/lib/utils'
import { useFacebookStore } from '@/stores/facebook.store'
import { InstallPlatform, useLimitReachedStore } from '@/stores/limit-usage.store'
import { useNotificationStore } from '@/stores/notification.store'
import { Organization } from '@/types/organization'
import { User } from '@/types/user'

import { AppLogo } from '../app-logo'
import DashboardLoadingIcon from '../icon/dashboard-loading-icon'
import { FacebookLogoSidebar } from '../icon/sidebar/facebook-logo-sidebar'
import { HistoryMenuItemDot } from '../icon/sidebar/history-menu-item-dot'
import { WordpressLogoSidebarIcon } from '../icon/sidebar/wordpress-logo-sidebar'
import { AlertModal } from '../modals/alert-modal'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Skeleton } from '../ui/skeleton'

interface NavItemProps {
  onOpenChange?: () => void
  setIsOpenUseSetting: Dispatch<SetStateAction<boolean>>
  setIsOpenFeedback: Dispatch<SetStateAction<boolean>>
  setIsOpenLegal: Dispatch<SetStateAction<boolean>>
  onNavigate: (_href: string) => void
  canAccessInternalAnswerSearch?: boolean
}

export const DashboardNav: React.FC<NavItemProps> = ({
  setIsOpenUseSetting,
  setIsOpenFeedback,
  setIsOpenLegal,
  onNavigate,
  canAccessInternalAnswerSearch = false,
}) => {
  const t = useTranslations('Common.Components.layout.dashboard')
  const { session, isLoading: isLoadingAuth } = useAuth()
  const signOut = useSignOut()
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const pathname = usePathname()
  const limitStore = useLimitReachedStore((state) => state.limitReach)
  const isFacebookMessengerAllowed = limitStore?.platforms?.includes(InstallPlatform.FACEBOOK_MESSENGER)
  const isFacebookLoggedIn = useFacebookStore((state) => state.isFacebookLoggedIn)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const { unreadCount, isLoadingUnreadCount } = useNotificationStore()
  const isSidebarVisible = useSidebarVisibility()

  const hasUnreadNotifications = useMemo(() => {
    return unreadCount > 0
  }, [unreadCount])

  const isActivePath = (path: string) => {
    if (path === '/dashboard' && pathname === '/dashboard') {
      return true
    }

    // @NOTE: need to edit logic if we have subpages (e.g. training/file) if we want to highlight parent page
    //        current use case of current logic is to stop highlight (becase we were using startsWith)
    //        when we have a 404 page but the url is similar to one of the existing pages
    const queryIndex = pathname?.indexOf('?')
    return path !== '/dashboard' && pathname?.slice(0, queryIndex === -1 ? pathname.length : queryIndex) === path
  }

  const onLogoutModalConfirm = async () => {
    await signOut()
  }

  const { setOpenMobile } = useSidebar()

  const handleNavClick = (href: string) => {
    onNavigate(href)
    setOpenMobile(false)
  }

  // Check role permissions
  const hasSubscriptionAccess = usePermission(PermissionResource.SUBSCRIPTION, { action: PermissionAction.WRITE })
  const hasChatbotAccess = usePermission(PermissionResource.CHATBOT, { action: PermissionAction.WRITE })
  const hasFacebookAccess = usePermission(PermissionResource.FACEBOOK, { action: PermissionAction.WRITE })
  const hasWordpressAccess = usePermission(PermissionResource.WORDPRESS, { action: PermissionAction.WRITE })
  const hasApiKeyAccess = usePermission(PermissionResource.API_KEY, { action: PermissionAction.WRITE })
  const hasMCPSeverAccess = usePermission(PermissionResource.MCP_SERVER, { action: PermissionAction.WRITE })
  const hasOrganizationAccess = usePermission(PermissionResource.ORGANIZATION, {
    action: PermissionAction.WRITE,
  })

  const isDeveloperEnabled = useMemo(() => {
    return hasApiKeyAccess && hasMCPSeverAccess
  }, [hasApiKeyAccess, hasMCPSeverAccess])

  const canShowInternalAnswerSearch = useMemo(() => {
    return canAccessInternalAnswerSearch
  }, [canAccessInternalAnswerSearch])

  const isFacebookMessengerEnabled = useMemo(() => {
    return isFacebookMessengerAllowed && isFacebookLoggedIn && hasFacebookAccess
  }, [isFacebookMessengerAllowed, isFacebookLoggedIn, hasFacebookAccess])

  return (
    <>
      <Sidebar className="z-20 border-none" collapsible="icon">
        <SidebarHeader
          className={cn(
            'flex-row items-center gap-0 bg-base-background-layout',
            isSidebarVisible ? 'h-[3.75rem] justify-between p-5 ps-7' : 'h-16 justify-center',
          )}
        >
          {isSidebarVisible && <AppLogo />}
          <X
            className="relative -right-1 block !h-6 !w-6 cursor-pointer text-foreground opacity-50 md:hidden"
            onClick={() => setOpenMobile(false)}
          />
          <SidebarTrigger
            className={cn(
              BASE_SIDEBAR_MENU_ITEM_CLASSNAME,
              'hidden cursor-pointer items-center justify-center p-0 md:flex',
            )}
            asChild
          >
            <Button variant={'noneHover'} className="h-9 w-9 rounded-lg">
              <PanelLeft className="!size-5 text-base-sidebar-foreground" />
            </Button>
          </SidebarTrigger>
        </SidebarHeader>

        <SidebarContent className="gap-1 bg-base-background-layout p-4 group-data-[collapsible=icon]:overflow-y-auto group-data-[collapsible=icon]:px-2">
          <SidebarMenu>
            <StyledSidebarMenuItem
              path="/dashboard"
              isActivePath={isActivePath}
              handleNavClick={(paramPath: string) => handleNavClick(`${paramPath}`)}
              Icon={LayoutDashboardIcon}
              label={t('nav.dashboard')}
            />

            <div className="hidden md:block">
              <Popover open={isNotificationOpen} onOpenChange={setIsNotificationOpen}>
                <PopoverTrigger tabIndex={-1} className="w-full">
                  <NotificationMenuItem
                    isActivePath={isActivePath}
                    hasUnreadNotifications={hasUnreadNotifications}
                    isLoading={isLoadingUnreadCount}
                  />
                </PopoverTrigger>
                <PopoverContent
                  className="border-base h-screen max-h-[31.25rem] w-[20.3125rem] overflow-hidden rounded-xl p-0 shadow-xl"
                  align="start"
                  side="right"
                  sideOffset={20}
                  alignOffset={-108}
                >
                  <NotificationManagement onNotificationDisplayClick={() => setIsNotificationOpen(false)} />
                </PopoverContent>
              </Popover>
            </div>

            <NotificationMenuItem
              className="md:hidden"
              path="/dashboard/notifications"
              isActivePath={isActivePath}
              handleNavClick={handleNavClick}
              hasUnreadNotifications={hasUnreadNotifications}
              isLoading={isLoadingUnreadCount}
            />

            <StyledSidebarMenuItem
              path="/dashboard/history"
              isActivePath={isActivePath}
              handleNavClick={handleNavClick}
              renderIcon={() => {
                if (!isSidebarVisible && limitStore && limitStore?.escalationCount > 0) {
                  return (
                    <div className="shrink-0">
                      <HistoryMenuItemDot />
                    </div>
                  )
                }
                return <History className="size-5 shrink-0" />
              }}
              label={t('nav.chat_history')}
              renderFooter={() => {
                return limitStore && limitStore.escalationCount > 0 ? (
                  <EscalationIconWithCount count={limitStore.escalationCount} />
                ) : null
              }}
            />

            <StyledSidebarMenuItem
              path="/dashboard/training"
              isActivePath={isActivePath}
              handleNavClick={handleNavClick}
              Icon={Sparkles}
              label={t('nav.training')}
            />

            {hasChatbotAccess && (
              <StyledSidebarMenuItem
                path="/dashboard/appearance"
                isActivePath={isActivePath}
                handleNavClick={handleNavClick}
                Icon={Paintbrush}
                label={t('nav.appearance')}
              />
            )}

            {hasChatbotAccess && (
              <StyledSidebarMenuItem
                path="/dashboard/install"
                isActivePath={isActivePath}
                handleNavClick={handleNavClick}
                Icon={MonitorUp}
                label={t('nav.install')}
              />
            )}

            {hasOrganizationAccess && (
              <StyledSidebarMenuItem
                path="/dashboard/organization"
                isActivePath={isActivePath}
                handleNavClick={handleNavClick}
                Icon={Users}
                label={t('nav.organization')}
              />
            )}
            {isDeveloperEnabled && (
              <StyledSidebarMenuItem
                path="/dashboard/developer"
                isActivePath={isActivePath}
                handleNavClick={handleNavClick}
                Icon={Code2}
                label={t('nav.developer')}
              />
            )}
            {canShowInternalAnswerSearch && (
              <StyledSidebarMenuItem
                path="/dashboard/internal-answer-search"
                isActivePath={isActivePath}
                handleNavClick={handleNavClick}
                Icon={Library}
                label={t('nav.internal_answer_search')}
              />
            )}
          </SidebarMenu>

          {(isFacebookMessengerEnabled || hasWordpressAccess) && <Separator className="my-3 bg-base-border" />}

          <SidebarMenu>
            {isFacebookMessengerEnabled && (
              <div className="animate-slide-down">
                <StyledSidebarMenuItem
                  path="/dashboard/facebook"
                  isActivePath={isActivePath}
                  handleNavClick={handleNavClick}
                  renderIcon={() => <FacebookLogoSidebar />}
                  label={t('nav.facebookMessenger')}
                />
              </div>
            )}
            {hasWordpressAccess && (
              <StyledSidebarMenuItem
                path="/dashboard/wordpress"
                isActivePath={isActivePath}
                handleNavClick={handleNavClick}
                renderIcon={() => <WordpressLogoSidebarIcon />}
                label={t('nav.wordpress')}
              />
            )}
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter className="bg-base-background-layout p-4 group-data-[collapsible=icon]:px-2">
          <SidebarMenu>
            <StyledSidebarMenuItem
              Icon={Send}
              label={t('dropdown.feedback')}
              onButtonClick={() => setIsOpenFeedback(true)}
            />

            <SidebarMenuItem
              className={cn(
                'rounded-lg hover:bg-base-sidebar-accent',
                BASE_SIDEBAR_MENU_ITEM_CLASSNAME,
                !isSidebarVisible && 'h-12 justify-center',
              )}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className={
                      (cn('flex h-16 flex-row items-center gap-2 py-3.5 pe-2 ps-4'),
                      isSidebarVisible ? 'justify-start' : 'justify-center')
                    }
                    tooltip={session?.user?.name}
                  >
                    {isSidebarVisible ? (
                      <>
                        <UserInfo
                          className="flex-1 truncate"
                          isLoading={isLoadingAuth}
                          userName={session?.user?.name}
                          organizationName={session?.user?.activeOrganization?.name}
                        />
                        <ChevronsUpDown className="ms-auto size-4 shrink-0" />
                      </>
                    ) : (
                      <UserRound className="!size-5 text-base-sidebar-foreground" />
                    )}
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  className={cn('h-full', isSidebarVisible ? 'w-[--radix-popper-anchor-width]' : 'ml-2 w-60')}
                >
                  {hasSubscriptionAccess && (
                    <ManagePlanButton
                      renderAppSumoLink={({ href, target }) => (
                        <DropdownMenuItem asChild>
                          <Link href={href} target={target} className="gap-2 py-2">
                            <Sparkles className="h-4 w-4" />
                            <span>{t('dropdown.updateToPro')}</span>
                          </Link>
                        </DropdownMenuItem>
                      )}
                      renderBillingDialogTrigger={({ onClick }) => {
                        return (
                          <DropdownMenuItem onClick={onClick} className="gap-2 py-2">
                            <Sparkles className="h-4 w-4" />
                            <span>{t('dropdown.updateToPro')}</span>
                          </DropdownMenuItem>
                        )
                      }}
                      renderAppSumoLinkChildren={() => t('dropdown.updateToPro')}
                      renderBillingDialogChildren={() => t('dropdown.updateToPro')}
                    />
                  )}

                  {hasSubscriptionAccess && <DropdownMenuSeparator />}
                  <DropdownMenuItem className="gap-2 py-2" onClick={() => setIsOpenLegal(true)}>
                    <Info className="h-4 w-4" />
                    <span>{t('dropdown.legal')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 py-2" onClick={() => setIsOpenFeedback(true)}>
                    <Send className="h-4 w-4" />
                    <span>{t('dropdown.feedback')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 py-2" onClick={() => setIsOpenUseSetting(true)}>
                    <Settings className="h-4 w-4" />
                    <span>{t('dropdown.userPreferences')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 py-2" onClick={() => setIsLogoutModalOpen(true)}>
                    <LogOut className="h-4 w-4" />
                    <span>{t('dropdown.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <AlertModal
        title={t('logout.titleDialog')}
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={() => onLogoutModalConfirm()}
        isShowDescription={false}
        titleButtonSubmit={t('logout.title')}
      />
    </>
  )
}

const BASE_SIDEBAR_MENU_ITEM_CLASSNAME =
  'flex cursor-pointer items-center rounded-lg text-base-sidebar-foreground hover:bg-base-sidebar-accent'
type StyledSidebarMenuItemProps = {
  path?: string
  isActivePath?: (_path: string) => boolean
  handleNavClick?: (_path: string) => void
  onButtonClick?: () => void
  Icon?: LucideIcon
  renderIcon?: () => React.ReactNode
  label: string
  renderFooter?: () => React.ReactNode
  className?: string
}
const StyledSidebarMenuItem = ({
  path,
  isActivePath,
  handleNavClick,
  onButtonClick,
  Icon,
  renderIcon,
  label,
  renderFooter,
  className,
}: StyledSidebarMenuItemProps) => {
  const isSidebarVisible = useSidebarVisibility()
  const onClick = () => {
    if (onButtonClick) {
      return onButtonClick()
    }
    if (handleNavClick && path) {
      return handleNavClick(path)
    }
  }

  return (
    <SidebarMenuItem
      className={cn(
        BASE_SIDEBAR_MENU_ITEM_CLASSNAME,
        {
          'bg-base-accent': path && isActivePath && isActivePath(path),
        },
        className,
        !isSidebarVisible && 'h-12 justify-center',
      )}
    >
      <SidebarMenuButton
        size="lg"
        asChild
        tooltip={
          renderFooter
            ? {
                children: (
                  <div className="flex items-center gap-2">
                    <span>{label}</span>
                    {renderFooter()}
                  </div>
                ),
              }
            : { children: label }
        }
      >
        <div
          className={cn('flex items-center gap-3 px-3 py-3.5', isSidebarVisible ? 'justify-between' : 'justify-center')}
          onClick={onClick}
        >
          <div className="flex items-center gap-3">
            {renderIcon ? renderIcon() : Icon && <Icon className="size-5 text-base-sidebar-foreground" />}
            {isSidebarVisible && <span className="truncate">{label}</span>}
          </div>
          {isSidebarVisible && renderFooter && renderFooter()}
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
StyledSidebarMenuItem.displayName = 'StyledSidebarMenuItem'

const StyledButtonSidebarMenuItem = ({
  label,
  renderIcon,
  renderFooter,
  Icon,
  className,
}: Pick<StyledSidebarMenuItemProps, 'label' | 'renderIcon' | 'renderFooter' | 'Icon'> & { className?: string }) => {
  // copied attributes from SidebarMenuItem and SidebarMenuButton
  return (
    <li
      data-sidebar="menu-item"
      className={cn(BASE_SIDEBAR_MENU_ITEM_CLASSNAME, 'group/menu-item relative', className)}
    >
      <div
        data-sidebar="menu-button"
        className="h-12 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!p-0"
      >
        <div className="flex items-center justify-between gap-3 px-3 py-3.5">
          <div className="flex items-center gap-3">
            {renderIcon ? renderIcon() : Icon && <Icon className="size-5" />}
            <span>{label}</span>
          </div>
          {renderFooter && renderFooter()}
        </div>
      </div>
    </li>
  )
}
StyledButtonSidebarMenuItem.displayName = 'StyledButtonSidebarMenuItem'

const NotificationMenuItem = ({
  hasUnreadNotifications,
  isLoading = false,
  ...props
}: { hasUnreadNotifications: boolean; isLoading?: boolean } & Omit<
  StyledSidebarMenuItemProps,
  'label' | 'renderIcon' | 'Icon'
>) => {
  const t = useTranslations('Common.Components.layout.dashboard')
  return (
    <StyledSidebarMenuItem
      {...props}
      renderIcon={() => {
        if (isLoading) {
          return (
            <div className="relative">
              <Bell className="size-5" />
              <DashboardLoadingIcon className="absolute -right-px -top-0.5 size-2" />
            </div>
          )
        }
        return (
          <div
            className={cn('relative', {
              'after:absolute after:right-px after:top-0.5 after:size-2 after:rounded-full after:bg-base-destructive after:content-[""]':
                hasUnreadNotifications,
            })}
          >
            {hasUnreadNotifications ? <BellDot className="size-5" /> : <Bell className="size-5" />}
          </div>
        )
      }}
      label={t('nav.notification')}
    />
  )
}

const UserInfo = ({
  userName,
  organizationName,
  isLoading,
  className,
}: {
  userName?: User['name']
  organizationName?: Organization['name']
  isLoading: boolean
  className?: string
}) => {
  const shouldShowSkeleton = isLoading && !userName && !organizationName
  const userTitle = userName && organizationName ? `${userName} - ${organizationName}` : userName || organizationName
  return (
    <div className={cn('flex items-center gap-3', className)}>
      {shouldShowSkeleton ? (
        <Skeleton className="h-12 w-full" />
      ) : (
        <div className="flex flex-col gap-0.5 truncate text-base-sidebar-foreground" title={userTitle}>
          {userName && <p className="truncate whitespace-pre text-base font-semibold">{userName}</p>}
          {organizationName && <p className="truncate whitespace-pre text-sm">{organizationName}</p>}
        </div>
      )}
    </div>
  )
}
